import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalUser, ProjectInfo, Incidence } from '../../../types';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { KlSheet } from '../modal/KlSheet';
import { KlDialog } from '../modal/KlDialog';

export interface MobileReportBugSheetProps {
    open: boolean;
    onClose: () => void;
    currentUser?: GlobalUser;
    currentProject?: ProjectInfo & { id: string };
    onAddIncidence: (incidence: Incidence) => void;
    origin?: { x: number; y: number } | null;
}

export const MobileReportBugSheet: React.FC<MobileReportBugSheetProps> = ({
    open,
    onClose,
    currentUser,
    currentProject,
    onAddIncidence,
    origin
}) => {
    const [moduleName, setModuleName] = useState('Otros / General');
    const [actionDescription, setActionDescription] = useState('');
    const [expectedResult, setExpectedResult] = useState('');
    const [actualResult, setActualResult] = useState('');
    const [priority, setPriority] = useState<'Baja' | 'Media' | 'Alta'>('Media');
    const [screenshot, setScreenshot] = useState<string | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

    const location = useLocation();

    // Auto-detect module name based on current route
    useEffect(() => {
        const path = location.pathname;
        if (path === '/') setModuleName('Dashboard / Inicio');
        else if (path === '/edt') setModuleName('EDT (Estructura de Trabajo)');
        else if (path === '/planning') setModuleName('Planificación');
        else if (path === '/quality') setModuleName('Control de Calidad');
        else if (path === '/payments') setModuleName('Subcontratos y Pagos');
        else if (path === '/reports') setModuleName('Informes');
        else setModuleName('Otros / General');
    }, [location.pathname, open]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setErrorDialogMessage("La imagen excede el límite de 5MB. Por favor, selecciona una más pequeña.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setScreenshot(event.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobilePhotoCapture = async () => {
        try {
            const image = await Camera.getPhoto({
                quality: 80,
                allowEditing: false,
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Prompt,
                promptLabelHeader: 'Adjuntar Imagen de Incidencia',
                promptLabelPhoto: 'Seleccionar de Galería',
                promptLabelPicture: 'Tomar Fotografía'
            });

            if (image.dataUrl) {
                const sizeInBytes = (image.dataUrl.length * 3) / 4;
                if (sizeInBytes > 5 * 1024 * 1024) {
                    setErrorDialogMessage("La imagen excede el límite de 5MB. Por favor, selecciona una más pequeña.");
                    return;
                }
                setScreenshot(image.dataUrl);
            }
        } catch (error) {
            console.log("User cancelled or camera error:", error);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!actionDescription.trim() || !expectedResult.trim() || !actualResult.trim()) return;

        setIsSubmitting(true);

        const newIncidence: Incidence = {
            id: `INC-${Date.now()}`,
            userEmail: currentUser?.email || 'anonimo@ldconstructora.cl',
            userName: currentUser?.name || 'Usuario Invitado',
            projectId: currentProject?.id || 'GLOBAL',
            projectName: currentProject?.name || 'General / Ninguna',
            moduleName,
            actionDescription: actionDescription.trim(),
            expectedResult: expectedResult.trim(),
            actualResult: actualResult.trim(),
            priority,
            status: 'Pendiente',
            creationDate: new Date().toISOString(),
            screenshot
        };

        setTimeout(() => {
            onAddIncidence(newIncidence);
            setIsSubmitting(false);
            setIsSuccess(true);

            setActionDescription('');
            setExpectedResult('');
            setActualResult('');
            setPriority('Media');
            setScreenshot(undefined);

            setTimeout(() => {
                setIsSuccess(false);
                onClose();
            }, 2000);
        }, 800);
    };

    return (
        <KlSheet
            open={open}
            onClose={onClose}
            height="fraction"
            fixedHeight="80%"
            origin={origin}
            dragHandle={false}
            showCloseButton={false}
            blurBackdrop={true}
            contentClassName="p-0 bg-[#F1F5F9] dark:bg-slate-900 overflow-hidden flex flex-col"
            footer={
                !isSuccess ? (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !actionDescription.trim() || !expectedResult.trim() || !actualResult.trim()}
                        className="w-full h-11 rounded-[12px] font-bold text-sm bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 dark:disabled:bg-slate-800 disabled:text-slate-400 text-white flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-all"
                    >
                        {isSubmitting ? (
                            <>
                                <span className="material-symbols-outlined text-[18px] animate-spin">sync</span>
                                <span>Registrando falla...</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                                    <path d="m21.854 2.147-10.94 10.939" />
                                </svg>
                                <span>Registrar falla</span>
                            </>
                        )}
                    </button>
                ) : null
            }
        >
            {/* Header Rojo borde a borde 52px + tirador */}
            <div className="relative bg-[#DC2626] text-white flex items-center gap-2.5 px-3 pt-4 pb-2 shrink-0 shadow-xs">
                {/* Tirador blanco al 60% de opacidad */}
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-9 h-1 rounded-full bg-white/60 pointer-events-none" />

                <span className="w-8 h-8 rounded-[9px] bg-white/20 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2.5" y="3.5" width="15" height="12" rx="2" />
                        <path d="M2.5 7.5h15" />
                        <path d="m8 9.6 4 4" />
                        <path d="m12 9.6-4 4" />
                        <path d="M18 12.5 22.5 20.5H13.5z" fill="#DC2626" />
                        <path d="M18 15.8v2" />
                        <path d="M18 19.5h.01" />
                    </svg>
                </span>

                <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-bold block truncate leading-tight text-white m-0">Reportar falla técnica</h3>
                    <div className="text-[11px] text-white/90 opacity-90 truncate mt-0.5">Registro directo para soporte de código</div>
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="w-8 h-8 text-white hover:bg-white/10 rounded-full flex items-center justify-center shrink-0 active:scale-95 transition-transform"
                >
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
            </div>

            {/* Contenido scrolleable */}
            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2.5 bg-[#F1F5F9] dark:bg-slate-900">
                {isSuccess ? (
                    <div className="p-8 flex flex-col items-center justify-center text-center gap-3 my-auto">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-sm">
                            <span className="material-symbols-outlined text-[36px] font-bold">check_circle</span>
                        </div>
                        <b className="text-[18px] text-[#0F172A] dark:text-white">¡Reporte Registrado!</b>
                        <p className="text-[13px] text-[#64748B] dark:text-slate-400 max-w-xs">
                            La incidencia ha sido enviada con éxito. El equipo técnico la revisará directamente en el código.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Tarjeta Usuario y Obra (Maqueta 12) */}
                        <div className="bg-white dark:bg-slate-800 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-700/60 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">Usuario</div>
                                <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 truncate mt-0.5">
                                    {currentUser?.name || 'Simón (administrador)'}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">Obra activa</div>
                                <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 truncate mt-0.5">
                                    {currentProject?.name || '318 - 94 Viviendas Las Quilas'}
                                </div>
                            </div>
                        </div>

                        {/* Tarjeta con formulario */}
                        <div className="bg-white dark:bg-slate-800 rounded-[14px] p-3.5 shadow-xs border border-slate-100 dark:border-slate-700/60 flex flex-col gap-3">
                            {/* Módulo o sección */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    Módulo o sección
                                </label>
                                <div className="relative">
                                    <select
                                        value={moduleName}
                                        onChange={(e) => setModuleName(e.target.value)}
                                        className="w-full h-10 px-3 pr-8 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] font-medium text-[#0F172A] dark:text-slate-100 outline-none appearance-none"
                                    >
                                        <option>Dashboard / Inicio</option>
                                        <option>EDT (Estructura de Trabajo)</option>
                                        <option>Planificación</option>
                                        <option>Control de Calidad</option>
                                        <option>Subcontratos y Pagos</option>
                                        <option>Informes</option>
                                        <option>Otros / General</option>
                                    </select>
                                    <svg className="w-4 h-4 text-[#64748B] absolute right-2.5 top-3 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </div>
                            </div>

                            {/* Acción que estabas ejecutando */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    Acción que estabas ejecutando
                                </label>
                                <input
                                    type="text"
                                    value={actionDescription}
                                    onChange={(e) => setActionDescription(e.target.value)}
                                    placeholder="Ej: Intentaba aprobar el Estado de Pago N°2…"
                                    className="w-full h-10 px-3 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] outline-none focus:border-[#2563EB]"
                                />
                            </div>

                            {/* ¿Qué resultado esperabas obtener? */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    ¿Qué resultado esperabas obtener?
                                </label>
                                <textarea
                                    rows={2}
                                    value={expectedResult}
                                    onChange={(e) => setExpectedResult(e.target.value)}
                                    placeholder="Describe la respuesta correcta que debió dar la plataforma…"
                                    className="w-full p-2.5 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] outline-none focus:border-[#2563EB] resize-none"
                                />
                            </div>

                            {/* ¿Qué error o comportamiento incorrecto ocurrió? */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    ¿Qué error o comportamiento incorrecto ocurrió?
                                </label>
                                <textarea
                                    rows={2}
                                    value={actualResult}
                                    onChange={(e) => setActualResult(e.target.value)}
                                    placeholder="Describe la falla, el mensaje de error o la inconsistencia detectada…"
                                    className="w-full p-2.5 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] outline-none focus:border-[#2563EB] resize-none"
                                />
                            </div>

                            {/* Adjuntar captura de pantalla */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    Adjuntar captura de pantalla
                                </label>
                                {!screenshot ? (
                                    Capacitor.isNativePlatform() ? (
                                        <button
                                            type="button"
                                            onClick={handleMobilePhotoCapture}
                                            className="w-full h-16 rounded-[12px] border-[1.5px] border-dashed border-[#CBD5E1] dark:border-slate-700 flex items-center justify-center gap-2 text-[#475569] dark:text-slate-300 text-[13px] font-semibold active:scale-[0.99] transition-transform"
                                        >
                                            <svg className="w-5 h-5 text-[#64748B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" />
                                                <circle cx="12" cy="13" r="3" />
                                            </svg>
                                            <span>Tomar foto o cargar desde galería</span>
                                        </button>
                                    ) : (
                                        <label className="w-full h-16 rounded-[12px] border-[1.5px] border-dashed border-[#CBD5E1] dark:border-slate-700 flex items-center justify-center gap-2 text-[#475569] dark:text-slate-300 text-[13px] font-semibold cursor-pointer active:scale-[0.99] transition-transform">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="hidden"
                                            />
                                            <svg className="w-5 h-5 text-[#64748B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" />
                                                <circle cx="12" cy="13" r="3" />
                                            </svg>
                                            <span>Cargar desde galería o archivo</span>
                                        </label>
                                    )
                                ) : (
                                    <div className="relative rounded-[12px] border border-slate-200 dark:border-slate-700 p-2 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between">
                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                            <img src={screenshot} alt="Captura" className="w-12 h-10 object-cover rounded-md border border-slate-200" />
                                            <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300 truncate">
                                                Captura cargada
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setScreenshot(undefined)}
                                            className="w-8 h-8 text-red-500 hover:text-red-700 flex items-center justify-center"
                                            title="Eliminar captura"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Gravedad / prioridad */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-[#64748B] dark:text-slate-400 uppercase tracking-wide">
                                    Gravedad / prioridad
                                </label>
                                <div className="relative">
                                    <select
                                        value={priority}
                                        onChange={(e) => setPriority(e.target.value as any)}
                                        className="w-full h-10 px-3 pr-8 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] font-medium text-[#0F172A] dark:text-slate-100 outline-none appearance-none"
                                    >
                                        <option value="Baja">Baja (Detalles visuales)</option>
                                        <option value="Media">Media (Afecta flujos secundarios)</option>
                                        <option value="Alta">Alta (Bloqueo operacional crítico)</option>
                                    </select>
                                    <svg className="w-4 h-4 text-[#64748B] absolute right-2.5 top-3 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <KlDialog
                open={Boolean(errorDialogMessage)}
                onClose={() => setErrorDialogMessage(null)}
                title="Archivo muy pesado"
                message={errorDialogMessage || ''}
                confirmText="Entendido"
                cancelText={null}
            />
        </KlSheet>
    );
};
